import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  PickupDeliveryLocationCreateRequestV1,
  PickupDeliveryLocationDeleteRequestV1,
  PickupDeliveryLocationListRequestV1,
  PickupDeliveryLocationListResponseV1,
  PickupDeliveryLocationResponseV1,
  PickupDeliveryLocationUpdateRequestV1,
} from '@forto-dev/customer-master-data-models'

import Client from '@/api/client'
import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { getConfig } from '@/app/config'

import { pickupDeliveryLocationsQueries } from './pickupDeliveryLocation.queries'
import { PickupDeliveryLocationListResponseV1Schema, PickupDeliveryLocationResponseV1Schema } from '../schema'

export const pickupDeliveryLocationConfig: ApiConfig<{
  listPickupDeliveryLocations: EndpointConfig<PickupDeliveryLocationListRequestV1, PickupDeliveryLocationListResponseV1>
  createPickupDeliveryLocation: EndpointConfig<PickupDeliveryLocationCreateRequestV1, PickupDeliveryLocationResponseV1>
  updatePickupDeliveryLocation: EndpointConfig<
    { request: PickupDeliveryLocationUpdateRequestV1; id: string },
    PickupDeliveryLocationResponseV1
  >
  deletePickupDeliveryLocation: EndpointConfig<
    { id: string; request: PickupDeliveryLocationDeleteRequestV1; accountID: string },
    void
  >
}> = {
  listPickupDeliveryLocations: {
    requestConfig: (params) => ({
      endpoint: '/v1/pickup-delivery-locations',
      method: 'get',
      options: {
        params,
      },
      deserializationStrategy: PickupDeliveryLocationListResponseV1Schema,
    }),
  },
  createPickupDeliveryLocation: {
    requestConfig: (request) => ({
      endpoint: '/v1/pickup-delivery-locations',
      method: 'post',
      body: request,
      deserializationStrategy: PickupDeliveryLocationResponseV1Schema,
    }),
  },
  updatePickupDeliveryLocation: {
    requestConfig: ({ request, id }) => ({
      endpoint: `/v1/pickup-delivery-locations/${id}`,
      method: 'put',
      body: request,
      deserializationStrategy: PickupDeliveryLocationResponseV1Schema,
    }),
  },
  deletePickupDeliveryLocation: {
    requestConfig: ({ id, request }) => ({
      endpoint: `/v1/pickup-delivery-locations/${id}`,
      method: 'delete',
      body: request,
    }),
  },
}

export const pickupDeliveryLocationClient = clientFactory(
  pickupDeliveryLocationConfig,
  new Client(`${getConfig().API_ENDPOINT ?? ''}/api/customer-master-data`)
)

export const usePickupDeliveryLocationCreateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: pickupDeliveryLocationClient.createPickupDeliveryLocation,
    onSuccess: async ({ accountID }) => {
      await queryClient.invalidateQueries(pickupDeliveryLocationsQueries.list({ accountID }))
    },
  })
}

export const usePickupDeliveryLocationUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: pickupDeliveryLocationClient.updatePickupDeliveryLocation,
    onSuccess: async ({ accountID }) => {
      await queryClient.invalidateQueries(pickupDeliveryLocationsQueries.list({ accountID }))
    },
  })
}

export const usePickupDeliveryLocationDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: pickupDeliveryLocationClient.deletePickupDeliveryLocation,
    onSuccess: async (_, { accountID }) => {
      await queryClient.invalidateQueries(pickupDeliveryLocationsQueries.list({ accountID }))
    },
  })
}
